'use client';

import React from 'react';

import BalancedContentSection from '@/components/sections/BalancedContentSection/BalancedContentSection';
import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import R2Image from '@/components/ui/R2Image';
import { LovedayStructuredText } from '@/features/loveday/components/LovedayStructuredText';
import type { LovedayImageTextSection } from '@/features/loveday/hooks/fetchData';
import { scrollToElement } from '@/utils';
import RichText from '@/components/ui/RichText/RichText';

interface ImageTextSectionProps {
  data?: LovedayImageTextSection;
  imagePosition?: 'left' | 'right';
  showButton?: boolean;
  isCenter?: boolean;
  alignTop?: boolean;
  matchHeight?: boolean;
  centerMediaOnTablet?: boolean;
  centerTextOnTablet?: boolean;
}

export const ImageTextSection: React.FC<ImageTextSectionProps> = ({
  data,
  imagePosition = 'left',
  showButton = false,
  isCenter = false,
  alignTop = false,
  matchHeight = false,
  centerMediaOnTablet = false,
  centerTextOnTablet = false,
}) => {
  if (!data) {
    return null;
  }

  const firstComponent = data.components?.[0];

  // imagePosition="left" → text on the right; imagePosition="right" → text on the left
  const balancedData = {
    textPosition: imagePosition === 'left' ? 'right' : 'left',
  } as const;

  // ── MEDIA SLOT ────────────────────────────────────────────────────────────
  const mediaContent = (
    <div className="flex w-full flex-col justify-center">
      {data.backgroundImage?.url && (
        <div
          className={`relative w-full overflow-hidden rounded-xl shadow-md ${
            matchHeight ? 'h-125 lg:h-135' : 'h-125'
          }`}
        >
          <R2Image
            path={data.backgroundImage.url}
            alt={data.backgroundImage.title || ''}
            className="object-cover"
          />
        </div>
      )}

      {/* Pricing notes box – shown under the image when imagePosition="left" (section5) */}
      {(firstComponent?.pricingRows || firstComponent?.pricingNotes) &&
        imagePosition === 'left' && (
          <div className="mt-3 rounded-xl border border-gray-100 bg-gray-100 p-4">
            {firstComponent.pricingRows ? (
              <LovedayStructuredText
                paragraphs={firstComponent.pricingRows}
                className="space-y-1"
                paragraphClassName="text-gray-700 text-base md:text-lg"
              />
            ) : (
              <ul className="list-none space-y-1 pl-0 text-base text-gray-700 md:text-lg">
                {firstComponent.pricingNotes?.map((note, i) => (
                  <li key={i}>{note}</li>
                ))}
              </ul>
            )}
          </div>
        )}

      {/* Footnotes – below the pricing box (section5) */}
      {firstComponent?.footnotes && imagePosition === 'left' && (
        <ul className="mt-4 list-disc space-y-0.5 pl-6 text-sm text-gray-700">
          {firstComponent.footnotes.map((note, i) => (
            <li key={i}>{note}</li>
          ))}
        </ul>
      )}

      {firstComponent?.footnoteNote && imagePosition === 'left' && (
        <LovedayStructuredText
          paragraphs={[firstComponent.footnoteNote]}
          className="mt-2"
          paragraphClassName="text-sm text-brand-purple leading-relaxed"
        />
      )}
    </div>
  );

  // ── TEXT SLOT ─────────────────────────────────────────────────────────────
  const textContent = (
    <>
      {data.heading && (
        <p
          className={`text-theme-primary text-h2 whitespace-pre-line ${isCenter ? 'text-center' : ''}`}
        >
          {data.heading}
        </p>
      )}

      {/* Plain paragraph description (section4, section6, section7) */}
      {data.descriptionParagraphs ? (
        <LovedayStructuredText
          paragraphs={data.descriptionParagraphs}
          className="space-y-4"
          paragraphClassName={`text-body  ${isCenter ? 'text-center' : ''}`}
        />
      ) : (
        data.description && (
          <RichText className={`text-body ${isCenter ? 'text-center' : ''}`}>
            {data.description}
          </RichText>
        )
      )}

      {/* Bullet list description (section5) */}
      {data.descriptionList && data.descriptionList.length > 0 && (
        <ul className="text-body list-disc space-y-1 pl-6">
          {data.descriptionList.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      )}

      {/* Benefits / list box – shown in the text slot when imagePosition="right" (section6) */}
      {firstComponent && imagePosition === 'right' && (
        <div className="mt-6 w-full rounded-2xl border border-gray-100 bg-gray-100 p-6 md:p-8">
          {firstComponent.heading && (
            <p className="text-theme-primary text-sm leading-[1.15] font-medium md:text-xl">
              {firstComponent.heading}
            </p>
          )}
          {firstComponent.benefitsList && (
            <ul className="mt-4 list-disc space-y-0.5 pl-6 text-base text-gray-700">
              {firstComponent.benefitsList.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {showButton && (
        <PrimaryButton
          className="mt-4"
          data={{ title: 'Enquire now', functionality: 'show_form' }}
          onShowForm={() => scrollToElement('form')}
          widthFitContent
        />
      )}
    </>
  );

  return (
    <BalancedContentSection
      data-testid="balanced-section"
      data={balancedData}
      mediaContent={mediaContent}
      textContent={textContent}
      containerClassName={`${matchHeight || alignTop ? 'content-wrapper lg:items-start!' : 'content-wrapper'} ${
        centerMediaOnTablet
          ? 'lg:[&>div:first-child]:self-center xl:[&>div:first-child]:self-start'
          : ''
      } ${
        centerTextOnTablet
          ? 'lg:[&>div:nth-child(2)]:self-center xl:[&>div:nth-child(2)]:self-start'
          : ''
      }`}
      spacingClassName="py-4 lg:py-5"
      gapClassName="gap-12 xl:gap-20"
    />
  );
};
